import { EntityPagingInfo } from '../common';
import { EntityView, EntityViewFilter, EntityViewRequestOptions, EntityViewSort } from './entity.view';
import { EntityViewMetadata } from './entity.view.metadata';
import { MetadataEntityData } from '../../../metadata';
import { ActivityEntityName } from '../../shared';
import { DEFAULT_PAGE_SIZE, DomainType } from '../../../utilities';
import { EntityViewType } from './enums';
import * as Enumerable from 'linq';

export interface EntityViewRequest {
    entity: ActivityEntityName;
    currentUserId?: string;
    domainType?: string;
}


export interface EntityHeaderRequest {
    viewId?: string;
    isEmbedded?: boolean;
    variant?: string;
    overrideMetadataEntity?: ActivityEntityName;
    overrideViewEntity?: ActivityEntityName;

    /** @deprecated - This is enforced by by the data strategy */
    domainType?: DomainType;
}

export interface EntityItemsRequest<T = any> {
    viewId?: string;
    parentId?: string;
    companyIds?: string[];
    search?: string;
    filters?: EntityViewFilter<T>[];
    entityIds?: Record<ActivityEntityName, string | string[]>;
    sort?: EntityViewSort,
    page?: number;
    pageSize?: number;
    isEmbedded?: boolean;
    variant?: string;

    /** @deprecated - This is enforced by by the data strategy */
    domainType?: DomainType;
}

export interface EntityFilterRequest {
    viewId?: string;
    parentId?: string;
    filterName?: string;
}


export interface EntityHeaderResponse<T = any> {
    metadata?: MetadataEntityData,
    views?: EntityView<T>[];
    view?: EntityView<T>;
}

export interface EntityItemsResponse<T = any> extends EntityPagingInfo {
    /** @deprecated */
    metadata?: MetadataEntityData,

    /** @deprecated */
    views?: EntityView<T>[];

    /** @deprecated */
    view?: EntityView<T>;
    items: T[];
}

export interface EntityCommandRequest<T = any> {
    commandId: string;
    data: T;
}

export function emptyEntityHeaderResponse() {
    return <EntityHeaderResponse>{
        metadata: null,
        views: [],
        items: [],
    };
}

export function emptyEntityItemsResponse() {
    return {
        total: 0,
        page: 0,
        pageSize: 0,
        views: [],
        items: [],
        metadata: null,
    } as EntityItemsResponse;
}

export function emptyEntityItemResponse() {
    return {
        item: null,
        metadata: null,
    };
}

export function getEntityItemsRequestFromView(view: EntityView): EntityItemsRequest {
    return {
        filters: view.filters,
        sort: view.sort,
        page: 0,
        pageSize: DEFAULT_PAGE_SIZE
    }
}

export function getEffectiveEntityItemsRequest(metadata: EntityViewMetadata, view: EntityView<any>, request: EntityItemsRequest<any>): EntityItemsRequest {

    const getSafeId = (filter: EntityViewFilter) => {
        const path = filter?.fieldId ?? filter._id;
        const safeId = path.endsWith('._id')
            ? path.replace('._id', '')
            : path;
        return safeId;
    }

    const viewFilterHash = Enumerable.from(view?.filters || []).toDictionary(f => getSafeId(f), f => f);
    const requestFilterHash = Enumerable.from(request?.filters || []).toDictionary(f => getSafeId(f), f => f);

    const effectiveFilters = [];

    Enumerable
        .from(metadata.systemFilters)
        .forEach(f => {

            const viewFilter = viewFilterHash.get(f?.attributeFilterName ?? f._id);
            const requestFilter = requestFilterHash.get(f?.attributeFilterName ?? f._id);
            const effectiveFilter = requestFilter || viewFilter;

            if (effectiveFilter) {
                effectiveFilters.push(effectiveFilter);
            }
        });

    return {
        search: request?.search?.trim(),
        filters: effectiveFilters,
        entityIds: request?.entityIds,
        parentId: request?.parentId ?? null,
        companyIds: request?.companyIds ?? [],
        sort: request?.sort ?? view?.sort,
        page: request?.page ?? 0,
        pageSize: request?.pageSize ?? DEFAULT_PAGE_SIZE
    }
}

export function getViewRequestOptions(request: EntityItemsRequest): EntityViewRequestOptions {
    const options: EntityViewRequestOptions = {};
    if (request?.variant) {
        options.variant = request.variant;
    }
    if (request?.isEmbedded) {
        options.type = EntityViewType.Embedded;
    }
    return options;
}